const STORAGE_KEY = 'visited_paths'
const MAX_ENTRIES = 10

type VisitedPathEntry = {
  readonly path: string
  readonly labelKey: string
  readonly labelFallback: string
  readonly detail: string
  readonly timestamp: number
}

function isValidEntry(v: unknown): v is VisitedPathEntry {
  if (typeof v !== 'object' || v === null) return false
  const obj = v as Record<string, unknown>
  return (
    typeof obj.path === 'string' &&
    obj.path.startsWith('/') &&
    !obj.path.includes('//') &&
    typeof obj.labelKey === 'string' &&
    typeof obj.labelFallback === 'string' &&
    typeof obj.timestamp === 'number'
  )
}

export function getVisitedPaths(): ReadonlyArray<VisitedPathEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isValidEntry)
  } catch {
    return []
  }
}

export function addVisitedPath({
  path,
  labelKey,
  labelFallback,
  detail,
}: {
  readonly path: string
  readonly labelKey: string
  readonly labelFallback: string
  readonly detail: string
}): void {
  try {
    const existing = getVisitedPaths()
    const withoutDuplicate = existing.filter(entry => entry.path !== path)
    const newEntry: VisitedPathEntry = {
      path,
      labelKey,
      labelFallback,
      detail,
      timestamp: Date.now(),
    }
    const updated = [newEntry, ...withoutDuplicate].slice(0, MAX_ENTRIES)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/**
 * Returns the most recent visited path that is NOT a /settings/* route.
 * Used by the "Powrót do aplikacji" (Back to app) button in the settings view.
 * Falls back to the provided path if no non-settings path has been visited.
 */
export function getLastAppPath(fallback: string): string {
  const entries = getVisitedPaths()
  const nonSettings = entries.find(entry => !entry.path.startsWith('/settings'))
  return nonSettings?.path ?? fallback
}

export function clearVisitedPaths(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // silently ignore
  }
}
